import { Fragment, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    CircularProgress,
    Alert,
    Chip,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { API_URL } from '../../api';
import HowItWorksCard from '../../components/HowItWorksCard';
import DoubleScrollBox from '../../components/DoubleScrollBox';
import { isBreakBefore } from '../../utils/registerBreaks';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const STATUS_CYCLE = { '': 'present', present: 'absent', absent: 'late', late: 'present' };
const STATUS_LABEL = { present: 'P', absent: 'A', late: 'L' };
const STATUS_FULL = { present: 'Present', absent: 'Absent', late: 'Late' };
const STATUS_COLOR = { present: 'success', absent: 'error', late: 'warning' };

const fmtShort = (d) =>
    new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const studentName = (s) => {
    if (!s) return '—';
    const me = `${s.firstName || ''} ${s.surname || ''}`.trim();
    if (s.role === 'parent' && s.childInfo?.studentName) return s.childInfo.studentName;
    return me;
};

export default function RegisterPage() {
    const [classes, setClasses] = useState([]);
    const [classId, setClassId] = useState('');
    const [lessons, setLessons] = useState([]);
    const [klass, setKlass] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [savingCell, setSavingCell] = useState(null);
    // Admin-note-per-lesson-per-student dialog. `noteDialog = { lesson, student }`.
    const [noteDialog, setNoteDialog] = useState(null);
    const [noteDraft, setNoteDraft] = useState('');
    const [savingNote, setSavingNote] = useState(false);

    const addAdminNote = async () => {
        if (!noteDialog) return;
        const text = noteDraft.trim();
        if (!text) return;
        setSavingNote(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/admin/lessons/${noteDialog.lesson._id}/admin-notes`,
                { studentId: noteDialog.student._id, text },
                authHeaders()
            );
            // Merge the updated lesson (which carries the new adminNotes) into local state.
            setLessons((all) => all.map((l) => (String(l._id) === String(data._id) ? { ...l, adminNotes: data.adminNotes } : l)));
            setNoteDraft('');
            setMessage('Note added — tutor will see the red-dot indicator on this cell.');
            // Close the dialog on success. If admin wants to add more, they
            // reopen it via the note icon — same click count as staying open.
            setNoteDialog(null);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to add note.');
        } finally {
            setSavingNote(false);
        }
    };

    const deleteAdminNote = async (noteId) => {
        if (!noteDialog) return;
        try {
            const { data } = await axios.delete(
                `${API_URL}/api/admin/lessons/${noteDialog.lesson._id}/admin-notes/${noteId}`,
                authHeaders()
            );
            setLessons((all) => all.map((l) => (String(l._id) === String(data._id) ? { ...l, adminNotes: data.adminNotes } : l)));
            // Keep dialog open so admin can add more if needed.
            setNoteDialog((d) => (d ? { ...d, lesson: { ...d.lesson, adminNotes: data.adminNotes } } : d));
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to delete note.');
        }
    };

    useEffect(() => {
        axios
            .get(`${API_URL}/api/admin/tutoring-classes?status=published`, authHeaders())
            .then((r) => setClasses(r.data || []))
            .catch(() => {});
    }, []);

    const loadClass = async (id) => {
        if (!id) return;
        setLoading(true);
        setError('');
        try {
            const [cls, list] = await Promise.all([
                axios.get(`${API_URL}/api/admin/tutoring-classes/${id}`, authHeaders()),
                axios.get(`${API_URL}/api/admin/lessons?classId=${id}`, authHeaders()),
            ]);
            setKlass(cls.data);
            setLessons(list.data || []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (classId) loadClass(classId); }, [classId]);

    const students = klass?.studentIds || [];

    const attendanceMap = useMemo(() => {
        // (lessonId, studentId) → attendance row
        const m = new Map();
        for (const l of lessons) {
            for (const a of l.attendance || []) {
                m.set(`${l._id}|${a.studentId}`, a);
            }
        }
        return m;
    }, [lessons]);

    const toggleLessonHeld = async (lesson) => {
        const next = !lesson.lessonHeld;
        try {
            await axios.patch(
                `${API_URL}/api/admin/lessons/${lesson._id}`,
                { lessonHeld: next },
                authHeaders()
            );
            setLessons((arr) => arr.map((l) => (l._id === lesson._id ? { ...l, lessonHeld: next } : l)));
        } catch {
            setError('Failed to update lesson.');
        }
    };

    const cycleStatus = async (lesson, student) => {
        const cur = attendanceMap.get(`${lesson._id}|${student._id}`)?.status || '';
        const next = STATUS_CYCLE[cur];
        setSavingCell(`${lesson._id}|${student._id}`);
        try {
            if (next === '') {
                // No "delete" endpoint — model marks 'absent' to remove the
                // tick rather than wiping. For now we treat the empty state
                // as a UI-only return-to-not-recorded; backend keeps last.
                // Simplest path: PATCH attendance to absent + comment 'cleared'.
                // For sub-C we just stick to upserting valid statuses; clearing
                // a cell is not in the spec.
                setSavingCell(null);
                return;
            }
            await axios.patch(
                `${API_URL}/api/admin/lessons/${lesson._id}/attendance`,
                { rows: [{ studentId: student._id, status: next }] },
                authHeaders()
            );
            // Re-fetch this lesson to refresh attendance snapshot.
            const { data } = await axios.get(`${API_URL}/api/admin/lessons?classId=${classId}`, authHeaders());
            setLessons(data || []);
        } catch {
            setError('Failed to save attendance.');
        } finally {
            setSavingCell(null);
        }
    };

    const exportCsv = () => {
        if (!lessons.length) return;
        const header = ['Date', ...students.map(studentName)];
        const rows = [header.join(',')];
        for (const lesson of lessons) {
            const cells = [
                fmtShort(lesson.date),
                ...students.map((s) => {
                    const a = attendanceMap.get(`${lesson._id}|${s._id}`);
                    return a?.status || '';
                }),
            ];
            rows.push(cells.join(','));
        }
        const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `register-${classId}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Class register</Typography>
                    
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button component={RouterLink} to="/tutoring-classes" variant="outlined">Classes</Button>
                    <Button onClick={exportCsv} variant="outlined" disabled={!lessons.length}>Export CSV</Button>
                </Stack>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            Select a class, then click the <strong>"Held?"</strong> icon to set whether the
                            lesson took place.<br />
                            <strong>Click once</strong> = lesson took place and attendance can be recorded.<br />
                            <strong>Click twice</strong> = lesson did not take place.<br /><br />
                            Then click an <strong>attendance button</strong> to change each student's status.
                            Each click cycles through the available options: <strong>Present (P)</strong>,
                            <strong> Absent (A)</strong>, <strong>Late (L)</strong>.<br /><br />
                            Changes save automatically — there is <strong>no separate save button</strong>.
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Card sx={{ p: 2, mb: 3 }}>
                <TextField
                    select
                    size="small"
                    label="Class"
                    value={classId}
                    onChange={(e) => setClassId(e.target.value)}
                    sx={{ minWidth: 320 }}
                >
                    <MenuItem value="">— Select a published class —</MenuItem>
                    {classes.map((c) => (
                        <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                    ))}
                </TextField>
            </Card>

            {loading && (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            )}

            {!loading && klass && lessons.length > 0 && (
                <>
                <Card sx={{ p: 2, mb: 2 }}>
                    <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" rowGap={1}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Held?</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <CheckCircleIcon color="success" fontSize="small" />
                            <Typography variant="caption">Took place</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <CancelIcon color="error" fontSize="small" />
                            <Typography variant="caption">Did NOT take place</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <AccessTimeIcon color="disabled" fontSize="small" />
                            <Typography variant="caption">Not yet recorded</Typography>
                        </Stack>

                        <Box sx={{ borderLeft: 1, borderColor: 'divider', height: 24, mx: 1 }} />

                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Attendance</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" label="P" color="success" sx={{ minWidth: 32 }} />
                            <Typography variant="caption">Present</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" label="A" color="error" sx={{ minWidth: 32 }} />
                            <Typography variant="caption">Absent</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" label="L" color="warning" sx={{ minWidth: 32 }} />
                            <Typography variant="caption">Late</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip size="small" label="—" sx={{ minWidth: 32 }} />
                            <Typography variant="caption">Not recorded</Typography>
                        </Stack>

                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            Click any chip to cycle the value
                        </Typography>
                    </Stack>
                </Card>

                <Card>
                    <DoubleScrollBox>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 140 }}>Date</TableCell>
                                <TableCell sx={{ minWidth: 80 }}>Held?</TableCell>
                                {students.map((s) => (
                                    <TableCell key={s._id} align="center" sx={{ minWidth: 64 }}>
                                        <Tooltip title={s.email || ''}>
                                            <Typography variant="caption">{studentName(s)}</Typography>
                                        </Tooltip>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lessons.map((lesson, i) => {
                                return (
                                <Fragment key={lesson._id}>
                                {isBreakBefore(lessons[i - 1], lesson) && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={2 + students.length}
                                            sx={{ p: 0, height: 32, bgcolor: 'grey.100', borderTop: 2, borderTopColor: 'grey.400' }}
                                        />
                                    </TableRow>
                                )}
                                <TableRow>
                                    <TableCell>{fmtShort(lesson.date)}</TableCell>
                                    <TableCell>
                                        <Tooltip title={
                                            lesson.lessonHeld === true ? 'Lesson took place — click to mark as not held'
                                            : lesson.lessonHeld === false ? 'Lesson did NOT take place — click to revert'
                                            : 'Not yet recorded — click to mark as held'
                                        }>
                                            <IconButton size="small" onClick={() => toggleLessonHeld(lesson)}>
                                                {lesson.lessonHeld ? (
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                ) : lesson.lessonHeld === false ? (
                                                    <CancelIcon color="error" fontSize="small" />
                                                ) : (
                                                    <AccessTimeIcon color="disabled" fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    {students.map((s) => {
                                        const a = attendanceMap.get(`${lesson._id}|${s._id}`);
                                        const status = a?.status || '';
                                        const key = `${lesson._id}|${s._id}`;
                                        const currentLabel = status ? STATUS_FULL[status] : 'Not recorded';
                                        const notesForCell = (lesson.adminNotes || []).filter(
                                            (n) => String(n.studentId) === String(s._id)
                                        );
                                        const hasNote = notesForCell.length > 0;
                                        const tip = hasNote
                                            ? `${notesForCell.length} admin note${notesForCell.length === 1 ? '' : 's'} — click chip to cycle attendance, click note button to view/edit`
                                            : `${currentLabel}. Click again to change status from: Present → Absent → Late`;
                                        return (
                                            <TableCell key={s._id} align="center" sx={{ p: 0.5 }}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Tooltip title={tip}>
                                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                            <Chip
                                                                size="small"
                                                                label={STATUS_LABEL[status] || '—'}
                                                                color={STATUS_COLOR[status] || 'default'}
                                                                onClick={() => cycleStatus(lesson, s)}
                                                                sx={{ minWidth: 38, cursor: 'pointer' }}
                                                                disabled={savingCell === key}
                                                            />
                                                            {hasNote && (
                                                                <Box
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: -3, right: -3,
                                                                        width: 10, height: 10,
                                                                        borderRadius: '50%',
                                                                        bgcolor: '#DC2626',
                                                                        border: '2px solid #fff',
                                                                        pointerEvents: 'none',
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Tooltip>
                                                    <Tooltip title="Add admin note (visible to tutor)">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setNoteDialog({ lesson, student: s })}
                                                            sx={{ opacity: hasNote ? 1 : 0.4, '&:hover': { opacity: 1 } }}
                                                        >
                                                            <StickyNote2OutlinedIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                                </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                    </DoubleScrollBox>
                </Card>
                </>
            )}

            {!loading && klass && lessons.length === 0 && (
                <Card sx={{ p: 4 }}>
                    <Typography align="center" color="text.secondary">
                        This class has no lessons generated yet. Re-publish it or run
                        "Regenerate lessons" from the class detail page.
                    </Typography>
                </Card>
            )}

            {/* Per-lesson-per-student admin note dialog.
                Use case: parent phones about a specific date ("Ella will
                be 15 min late today"). Admin adds a note here; the tutor
                sees a red dot on that cell in the tutor register. */}
            <Dialog
                open={!!noteDialog}
                onClose={() => { setNoteDialog(null); setNoteDraft(''); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Admin notes
                    {noteDialog && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {fmtShort(noteDialog.lesson.date)}
                            {' · '}
                            {noteDialog.student.firstName || ''} {noteDialog.student.surname || ''}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Notes are visible to the tutor on their register as a red dot on this cell.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="e.g. Ella will be 15 min late — mother called."
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                    />
                    <Button
                        onClick={addAdminNote}
                        variant="contained"
                        disabled={savingNote || !noteDraft.trim()}
                        sx={{ mt: 1, color: '#fff !important' }}
                    >
                        {savingNote ? 'Adding…' : 'Add note'}
                    </Button>
                    <Divider sx={{ my: 2 }} />
                    {(() => {
                        const notes = noteDialog
                            ? (noteDialog.lesson.adminNotes || []).filter(
                                (n) => String(n.studentId) === String(noteDialog.student._id)
                            )
                            : [];
                        if (notes.length === 0) {
                            return <Typography variant="body2" color="text.secondary">No notes yet.</Typography>;
                        }
                        return (
                            <Stack spacing={1.25} divider={<Divider />}>
                                {notes.slice().reverse().map((n) => (
                                    <Box key={n._id}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(n.at).toLocaleString('en-GB')}
                                                {n.byAdminEmail ? ` · ${n.byAdminEmail}` : ''}
                                            </Typography>
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => deleteAdminNote(n._id)}
                                                sx={{ minWidth: 0 }}
                                            >
                                                Delete
                                            </Button>
                                        </Stack>
                                        <Typography sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{n.text}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setNoteDialog(null); setNoteDraft(''); }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
